import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { SsoApi } from '@core/apis/sso-api';
import {
  copyForError,
  formatAttemptsRemaining,
  formatLockoutBody,
} from '@core/errors/error-catalogue';
import {
  INITIAL_CONTEXT,
  reduce,
  type SignInContext,
  type SignInEvent,
} from '@core/auth/sign-in-machine';
import { forgetPkce } from '@core/auth/pkce';
import type { AuthorizeRequest } from '@core/auth/authorize-request';
import type { OtpChallengeView, OtpChannel, SsoErrorBody } from '@core/models/sso.models';
import { firstValueFrom } from 'rxjs';

/**
 * Sequences the sign-in flow. Holds no rules of its own.
 *
 * ---------------------------------------------------------------------------------------------
 * THE ORDER OF CALLS IS THE PROTOTYPE'S, and each step consumes the handle the previous one issued:
 *
 *   /authorize      -> req                (performed by the browser; the SPA reads it off the URL)
 *   /identify       -> identity_ref       (needs: req)
 *   /password       -> mfa_token          (needs: identity_ref, req)
 *                   OR selection_token    (trusted device -- the OTP step is skipped)
 *   /mfa/send       -> a fresh challenge  (needs: mfa_token, channel)
 *   /mfa/verify     -> selection_token    (needs: mfa_token, code)
 *   /accounts       -> the eligible list  (needs: app, selection_token)
 *   /select-account -> the code           (needs: merchant_id, selection_token, app, req)
 *
 * Nothing here decides anything: `reduce()` says what state follows and `copyForError()` says what
 * the user reads. This class calls the API in the right order and feeds the results back in.
 * ---------------------------------------------------------------------------------------------
 */
@Injectable({ providedIn: 'root' })
export class SignInStore {
  private readonly api = inject(SsoApi);

  private readonly context = signal<SignInContext>(INITIAL_CONTEXT);
  private readonly authorizeRequest = signal<AuthorizeRequest | null>(null);

  readonly state = computed(() => this.context().state);
  readonly application = computed(() => this.context().application);
  readonly identifier = computed(() => this.context().identifier);
  readonly otp = computed(() => this.context().otp);
  readonly busy = computed(() => this.context().busy);
  readonly accounts = computed(() => this.context().accounts);

  /**
   * What the picker renders (GPGT-41335).
   *
   * A channel plus the mask the SERVICE disclosed for it, when it disclosed one. `/identify` names
   * one channel and masks only that destination -- it will not say which contacts exist to someone
   * who has not authenticated -- and `/password` returns the full list without masks. So a channel
   * is offered by name when its mask is unknown, rather than being hidden or guessed at.
   */
  readonly channelOptions = computed(() => {
    const context = this.context();

    return context.channels.map((channel) => ({
      channel,
      masked: channel === 'email' ? context.maskedEmail : (context.maskedPhone ?? null),
    }));
  });
  /** False on the direct entry, before the service has issued a `req`. */
  readonly hasHandle = computed(() => this.context().req !== null && this.context().req !== '');
  /** The handle itself, so "Back to sign in" can resume the flow rather than restart it. */
  readonly req = computed(() => this.context().req);
  readonly request = computed(() => this.authorizeRequest());
  private readonly handOff = computed(() => this.context().handOff);

  /**
   * The application code the flow is for.
   *
   * On the normal entry the SPA does not know it -- the handle does -- and the endpoints that need
   * it are the ones the service can answer from the handle. `''` is sent rather than a guess, and
   * the service resolves it from `req`.
   */
  private readonly appCode = computed(() => this.authorizeRequest()?.appCode ?? '');

  /** The error as the user should see it: copy from the catalogue, never the code. */
  readonly error = computed(() => {
    const { errorCode, retryAfterSeconds } = this.context();
    if (!errorCode) return null;
    const copy = copyForError(errorCode);
    return {
      ...copy,
      body:
        errorCode === 'account_locked'
          ? formatLockoutBody(copy.body, retryAfterSeconds ?? undefined)
          : copy.body,
    };
  });

  /**
   * The attempt counter, as a sentence or nothing.
   *
   * A SIGNAL rather than a template expression, and the copy comes from the catalogue: the page
   * renders `attemptsNotice()` and owns no wording. Null below the catalogue's threshold, so the
   * banner appears when the stake is near instead of sitting on the screen from the first render.
   */
  readonly attemptsNotice = computed(() =>
    formatAttemptsRemaining(this.context().otp?.attempts_remaining)
  );

  private dispatch(event: SignInEvent): void {
    this.context.update((current) => reduce(current, event));
  }

  /**
   * The normal entry: the service has already validated everything and issued a handle.
   *
   * There is no `AuthorizeRequest` here and there does not need to be — the redirect target and the
   * caller's `state` come back from `/select-account`, which returns the ones the service itself
   * validated. The SPA never reconstructs them.
   */
  beginWithHandle(req: string): void {
    this.authorizeRequest.set(null);
    this.context.set(INITIAL_CONTEXT);
    this.dispatch({
      type: 'AUTHORIZE_ACCEPTED',
      req,
      application: null,
    });
  }

  /**
   * The direct entry: OAuth parameters, no handle yet.
   *
   * The parameters are kept only to build the bounce to the service. The flow proper starts when
   * that bounce comes back to /login?req=…
   */
  beginDirect(request: AuthorizeRequest): void {
    this.authorizeRequest.set(request);
    this.context.set(INITIAL_CONTEXT);
    // The caller's `state` is NOT copied into this origin's storage. It was, and nothing ever read
    // it back -- `takeState` has no caller outside its own spec. A stored copy of someone else's
    // CSRF token is not a safeguard, it is a second source of truth for a value this origin only
    // forwards, and it is what made substituting the pair in `authorizeUrl` look deliberate.
  }

  /**
   * The bounce to `GET /authorize`, which FORWARDS the caller's `state` and `code_challenge`.
   *
   * It used to mint a fresh pair here and hand that to the service instead. Both values belong to
   * the APPLICATION that started the flow, and replacing them broke the flow twice over:
   *
   *   - `state` is the caller's CSRF token. The service binds what it is given and echoes it back
   *     at the end, so the application received a value it had never sent and refused the callback
   *     as a state mismatch -- after the user had signed in, chosen a channel, typed an OTP and
   *     picked an account.
   *   - `code_challenge` is the public half of a verifier only the application holds. The service
   *     bound this origin's challenge, so even with the state fixed the token exchange would have
   *     failed PKCE verification against the application's verifier.
   *
   * Nothing here needs a pair of its own. This origin never exchanges the authorization code -- it
   * hands `code` and `state` back to the application in `handOffUrl` -- so the verifier it used to
   * store was written and never read. `takeVerifier` and `takeState` have no caller outside their
   * own spec, which is the shape of that mistake.
   *
   * The parameters were already validated by `authorize.guard.ts` before any of this rendered, so
   * forwarding them cannot smuggle in a `redirect_uri` the service has not approved.
   */
  async authorizeUrl(request: AuthorizeRequest): Promise<string> {
    return this.api.buildAuthorizeUrl({
      app: request.appCode,
      redirectUri: request.redirectUri,
      state: request.state,
      codeChallenge: request.codeChallenge,
    });
  }

  async submitIdentifier(identifier: string): Promise<void> {
    const req = this.context().req;
    if (!req) return;
    this.dispatch({ type: 'SUBMIT' });
    try {
      const response = await firstValueFrom(
        this.api.identify({ identifier, app: this.appCode(), req })
      );
      this.dispatch({
        type: 'IDENTIFIED',
        identityRef: response.identity_ref,
        identifier,
        channels: response.mfa_channels as readonly OtpChannel[],
        // Whichever of the two the service chose to disclose. It masks the other as null on
        // purpose: naming both would tell an unauthenticated caller which contacts exist.
        maskedEmail: response.masked_email ?? null,
        maskedPhone: response.masked_phone ?? null,
      });
    } catch (error: unknown) {
      this.fail(error);
    }
  }

  async submitPassword(password: string, deviceFingerprint: string): Promise<void> {
    const { identityRef, req } = this.context();
    if (!identityRef || !req) return;
    this.dispatch({ type: 'SUBMIT' });
    try {
      const response = await firstValueFrom(
        this.api.submitPassword({ identityRef, password, deviceFingerprint, req })
      );

      if (response.status === 'mfa_required' && response.mfa_token) {
        /**
         * THE PASSWORD STEP DOES NOT SEND THE CODE. `/mfa/send` does, and `channel` is required, so
         * something has to decide it -- and now that decision is the USER'S when there is more than
         * one (GPGT-41335).
         *
         * An earlier version took `channels[0]`, which meant a code left for the identity's phone
         * before they had said a word. Two costs, and the second is the one that bites: a message
         * on a channel they may not be watching, and one of their `max_sends_per_window` spent
         * before they chose. Offering afterwards cannot undo either.
         *
         * ONE CHANNEL IS NOT A CHOICE, so it is sent straight away. A screen that asks a question
         * with a single answer is a click that teaches users to click without reading.
         */
        const channels = (response.channels ?? []) as readonly OtpChannel[];

        const only = channels.length === 1 ? channels[0] : null;
        if (only) {
          await this.send(response.mfa_token, only, 'MFA_REQUIRED');
          return;
        }

        // Zero lands here too, and deliberately shows the picker with nothing in it rather than
        // guessing a channel the service would refuse. `/password` derives the list from the
        // identity's own phone and email, so an empty one means neither exists -- which the screen
        // can say, and a silent `sent: true` for a code going nowhere cannot.
        this.dispatch({ type: 'CHANNELS_OFFERED', mfaToken: response.mfa_token, channels });
        return;
      }

      if (response.status === 'account_selection_required' && response.selection_token) {
        // A trusted device: the OTP step is skipped, the password never is.
        this.dispatch({ type: 'SELECTION_READY', selectionToken: response.selection_token });
        await this.loadAccounts();
        return;
      }

      this.dispatch({ type: 'FAILED', code: 'invalid_request' });
    } catch (error: unknown) {
      this.fail(error);
    }
  }

  /**
   * The user picked where the code should go (GPGT-41335).
   *
   * `MFA_REQUIRED` rather than `OTP_REISSUED`, because this is the FIRST delivery: nothing has been
   * sent yet, so there is no challenge to replace. The reducer accepts it from `channel_choice`.
   */
  async chooseChannel(channel: OtpChannel): Promise<void> {
    const { mfaToken } = this.context();
    if (!mfaToken) return;
    await this.send(mfaToken, channel, 'MFA_REQUIRED');
  }

  /** Back to the picker from the code screen. Sends nothing -- the point is to choose again first. */
  reselectChannel(): void {
    this.dispatch({ type: 'CHANNEL_RESELECTED' });
  }

  async resendOtp(channel?: OtpChannel): Promise<void> {
    const { mfaToken, otp } = this.context();
    if (!mfaToken) return;
    await this.send(mfaToken, channel ?? otp?.channel ?? 'email', 'OTP_REISSUED');
  }

  /**
   * One send, two meanings. Shared so the request, the busy flag and the failure path cannot drift
   * between the first delivery and a resend -- they differ only in which event follows.
   */
  private async send(
    mfaToken: string,
    channel: OtpChannel,
    event: 'MFA_REQUIRED' | 'OTP_REISSUED'
  ): Promise<void> {
    this.dispatch({ type: 'SUBMIT' });
    try {
      const sent = await firstValueFrom(this.api.sendOtp({ mfaToken, channel }));
      const otp = this.toOtpView(sent);
      this.dispatch(
        event === 'MFA_REQUIRED'
          ? { type: 'MFA_REQUIRED', mfaToken, otp }
          : { type: 'OTP_REISSUED', otp }
      );
    } catch (error: unknown) {
      this.fail(error);
    }
  }

  async verifyOtp(code: string, rememberDevice: boolean, deviceFingerprint: string): Promise<void> {
    const mfaToken = this.context().mfaToken;
    if (!mfaToken) return;
    this.dispatch({ type: 'SUBMIT' });
    try {
      const response = await firstValueFrom(
        this.api.verifyOtp({ mfaToken, code, rememberDevice, deviceFingerprint })
      );

      if (response.code) {
        // Verifying the code was enough to mint the authorization code: a single eligible account,
        // so the server skipped the picker for us.
        // /mfa/verify minted the code directly: one eligible account, so the server skipped the
        // picker. It returns the same triplet /select-account does.
        this.dispatch({
          type: 'AUTHORIZED',
          handOff: {
            code: response.code,
            state: response.state ?? '',
            redirectUri: response.redirect_uri ?? '',
          },
        });
        return;
      }
      if (response.selection_token) {
        this.dispatch({ type: 'SELECTION_READY', selectionToken: response.selection_token });
        await this.loadAccounts();
        return;
      }
      this.dispatch({ type: 'FAILED', code: 'invalid_request' });
    } catch (error: unknown) {
      this.fail(error);
    }
  }

  async chooseAccount(merchantId: string): Promise<void> {
    const { selectionToken, req } = this.context();
    if (!selectionToken || !req) return;
    this.dispatch({ type: 'SUBMIT' });
    try {
      const response = await firstValueFrom(
        this.api.selectAccount({ merchantId, selectionToken, app: this.appCode(), req })
      );
      this.dispatch({
        type: 'AUTHORIZED',
        handOff: {
          code: response.code,
          state: response.state,
          redirectUri: response.redirect_uri,
        },
      });
    } catch (error: unknown) {
      this.fail(error);
    }
  }

  /**
   * Loads the eligible accounts and lets the machine decide whether a picker is warranted.
   *
   * On an empty list the machine deliberately does not guess between `no_access_to_application` and
   * `no_merchant_account` — but this call cannot fail with those codes AND return a list, so an
   * empty list here means the server answered 200 with nothing, which is a contract violation rather
   * than a user-facing state. It is reported as `invalid_request`.
   */
  private async loadAccounts(): Promise<void> {
    const selectionToken = this.context().selectionToken;
    if (!selectionToken) return;
    try {
      const response = await firstValueFrom(
        this.api.accounts({ app: this.appCode(), selectionToken })
      );
      this.dispatch({
        type: 'ACCOUNTS_LOADED',
        accounts: response.accounts,
        skipSelection: response.skip_selection,
      });

      if (response.skip_selection && response.accounts[0]) {
        await this.chooseAccount(response.accounts[0].merchant_id);
        return;
      }
      if (response.accounts.length === 0)
        this.dispatch({ type: 'FAILED', code: 'invalid_request' });
    } catch (error: unknown) {
      this.fail(error);
    }
  }

  /**
   * The redirect back to the application. The last thing this origin does for a flow.
   *
   * BUILT FROM THE SERVER'S OWN VALUES. `/select-account` returns `{code, state, redirect_uri}` —
   * the redirect_uri it validated against the Application record, and the caller's state echoed
   * back. Reconstructing the URL from anything the SPA held would reintroduce a target the service
   * never approved, which is the whole failure FR-001 exists to prevent.
   */
  handOffUrl(): string | null {
    const handOff = this.handOff();
    if (!handOff) return null;
    const url = new URL(handOff.redirectUri);
    url.searchParams.set('code', handOff.code);
    url.searchParams.set('state', handOff.state);
    forgetPkce();
    return url.toString();
  }

  restart(): void {
    forgetPkce();
    this.dispatch({ type: 'RESTART' });
  }

  /** `/mfa/send`'s reply, in the shape the OTP screen renders. */
  private toOtpView(sent: {
    channel: string;
    masked_destination: string;
    expires_in: number;
    resend_after: number;
    attempts_remaining?: number;
  }): OtpChallengeView {
    return {
      destination_masked: sent.masked_destination,
      channel: sent.channel as 'sms' | 'email' | 'whatsapp',
      expires_at: new Date(Date.now() + sent.expires_in * 1000).toISOString(),
      /**
       * FROM THE SERVICE, and null when it did not say (GPGT-41748).
       *
       * This was the literal `3`. The service's policy default is five, so the number was wrong by
       * two from the moment the screen opened and stayed wrong for the life of the challenge — the
       * counter the mockups specify could not be built on it, which is why the redesign left the
       * counter out rather than render a lie.
       *
       * `?? null` rather than `?? 3` or `?? 5`: a service deployed before GPGT-41748 sends nothing
       * here, and the two ship on separate pipelines. Null renders no counter, which is the honest
       * answer to "we do not know".
       */
      attempts_remaining: sent.attempts_remaining ?? null,
      resend_available_at: new Date(Date.now() + sent.resend_after * 1000).toISOString(),
    };
  }

  /**
   * Turns a transport failure into a FAILED event.
   *
   * The service's envelope is `{error, message, reason?}` — the code is on `error`, not on `code`.
   * A network failure has no code at all and must not be reported as one, so it becomes the
   * catalogue's fallback rather than a made-up identifier.
   */
  private fail(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as SsoErrorBody | null;
      this.dispatch({
        type: 'FAILED',
        code: body?.error ?? 'network_error',
        retryAfterSeconds: body?.retry_after_seconds,
        // `SsoErrorBody` has always declared this and the service has always sent it on an OTP
        // refusal; only this line was missing, so every decrement was discarded on arrival.
        attemptsRemaining: body?.attempts_remaining,
      });
      return;
    }
    this.dispatch({ type: 'FAILED', code: 'network_error' });
  }
}
